import { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimDeprecationDialog } from './components/kim-deprecation-dialog';
import { ManualAnswerModal } from './components/manual-answer-modal';
import { NewKimDialog } from './components/new-kim-dialog';
import { KimOnboarding } from './components/onboarding/kim-onboarding';
import { ModeSelector } from './components/question/mode-selector';
import { QuestionInput } from './components/question/question-input';
import { QuestionSidebar } from './components/question/question-sidebar';
import { Permission } from '../../../../../../../../../backend/src/auth/permissions/permission';
import { ChecklistStep, DraftType } from '../../../../../../../../../backend/src/types/types';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useCreateMessage } from '@/api/mutations/useCreateMessage';
import { useCreateThread } from '@/api/mutations/useCreateThread';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import useGetUser from '@/api/queries/useGetUser';
import { QueryEntity } from '@/api/types';
import { sanitiseKimQuestion } from '@/helpers/sanitiseKimQuestion';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { SubsetChip } from '@/new/components/subset-chip';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { ExecuteKimSearch, KimMode } from '@/types';

const getTitleFromMode = (mode: KimMode) => {
  switch (mode) {
    case KimMode.QuestionFlow:
      return 'Ask a question...';
    case KimMode.ExtractionFlow:
      return 'Extract information...';
    case KimMode.DraftFlow:
      return 'Draft me a...';
  }
};

export function Kim() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Kim', path: 'kim' },
  });

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />
      <KimContent matterId={matterId} subsetId={subsetId} />
    </div>
  );
}

function KimContent({ matterId, subsetId }: { matterId: string; subsetId: string }) {
  const [questionValue, setQuestionValue] = useState('');
  const [draftType, setDraftType] = useState<DraftType | null>(null);
  const [mode, setMode] = useState<KimMode>(KimMode.QuestionFlow);
  const [taggedDocuments, setTaggedDocuments] = useState<{ doc_id: string; file_name: string }[]>([]);
  const [placeholder, setPlaceholder] = useState('Tell me about X...');
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [isManualAnswerModalOpen, setIsManualAnswerModalOpen] = useState(false);
  const [isNewKimDialogOpen, setIsNewKimDialogOpen] = useState(() => localStorage.getItem('isUsingNewKim') === null);
  const [isDeprecationDialogOpen, setIsDeprecationDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: responseDocIds } = useGetDocIds(matterId, subsetId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: user } = useGetUser();

  const { mutate: createThread, isLoading: isCreatingThread } = useCreateThread(matterId, subsetId);
  const { mutate: createMessage, isLoading: isCreatingMessage } = useCreateMessage();

  const hasCompletedKimOnboarding = user && !user.onboardingChecklistItems?.includes(ChecklistStep.UseKim);

  const resetState = () => {
    setQuestionValue('');
    setTaggedDocuments([]);
    setMode(KimMode.QuestionFlow);
    setDraftType(null);
  };

  const executeSearch: ExecuteKimSearch = async ({ question: value, draftType, questionMode }) => {
    if (!value || value.trim() === '') {
      return;
    }
    const question = sanitiseKimQuestion(value);
    createThread(question, {
      onSuccess: (data) => {
        createMessage({
          matterId,
          subsetId,
          question,
          docIds: taggedDocuments.map((doc) => doc.doc_id),
          draftType,
          mode: questionMode,
          threadId: data.id,
        });

        navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${data.id}`);
        trackCustomEvent(TrackingEventsV2.ThreadAskQuestion, {
          matterId,
          mode,
          isAllDocuments: subset?.isAllDocuments,
        });
      },
      onError: (error) => {
        console.error('Error creating thread and message');
        toast.error('Error processing your question. Please try again.');
      },
      onSettled: () => {
        resetState();
        queryClient.invalidateQueries({ queryKey: [QueryEntity.User] });
      },
    });
  };

  // Ask a question from the onboarding
  useEffect(() => {
    const { question, mode } = location.state || {};
    if (question && mode) {
      setQuestionValue(question);
      setMode(mode);
      executeSearch({ question: question, draftType: null, questionMode: mode });
    }
  }, [location.state]);

  useEffect(() => {
    trackPageView(PageName.Kim);
  }, []);

  useEffect(() => {
    setDraftType(null);
  }, [mode]);

  useEffect(() => {
    if (hasCompletedKimOnboarding) {
      setShowOnboarding(false);
    }
  }, [hasCompletedKimOnboarding]);

  const { hasPermission } = useHasPermission();
  const canCreateThread = hasPermission(Permission.ThreadCreate);
  const { getProcessingState } = useMatterProcessingStore();

  if (getProcessingState(matterId)?.hasCompletedRuns === false) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--bot" />
          </EmptyMedia>
          <EmptyTitle>Kim Unavailable</EmptyTitle>
          <EmptyDescription>Kim is unavailable while documents are still processing.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-1 items-center overflow-hidden">
      {(hasCompletedKimOnboarding || !canCreateThread) && (
        <>
          <QuestionSidebar taggedDocuments={taggedDocuments} />

          {canCreateThread && (
            <div className="absolute top-4 right-4 flex gap-2">
              {user?.organisation?.isLabellingOrg && (
                <Button
                  onClick={() => setIsManualAnswerModalOpen(true)}
                  className="flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                >
                  <span>+ Create Manual Answer</span>
                </Button>
              )}
              <Button
                onClick={() => {
                  if (!showOnboarding) {
                    trackCustomEvent(TrackingEventsV2.ThreadClickHelp);
                  }
                  setShowOnboarding(!showOnboarding);
                }}
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm"
              >
                <span
                  className={`${showOnboarding ? 'iconify lucide--arrow-left text-gray-500' : 'iconify lucide--circle-help text-teal-500'}`}
                ></span>

                <span>{showOnboarding ? 'Back' : 'Need some help?'}</span>
              </Button>
            </div>
          )}
        </>
      )}

      <div className="flex w-full flex-col items-center justify-center">
        {!canCreateThread ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--lock" />
              </EmptyMedia>
              <EmptyTitle>Permission Required</EmptyTitle>
              <EmptyDescription>
                You don't have permission to create new threads. Speak to an administrator to gain access.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : user && showOnboarding ? (
          <KimOnboarding
            executeSearch={executeSearch}
            setMode={setMode}
            setDraftType={setDraftType}
            setQuestionValue={setQuestionValue}
            subset={subset}
          />
        ) : (
          <div className="flex w-full max-w-4xl flex-col items-center px-4 pb-24">
            <div className="mb-16 -ml-1 flex flex-col items-center">
              <NewChip className="relative top-auto right-auto mb-2.75 -ml-1" />

              <Button asChild variant={'ghost'} className="">
                <Link
                  to={`/matters/${matterId}/subsets/${subsetId}/new-kim`}
                  className="group flex items-center rounded-full px-2 py-1 text-gray-600 ring-teal-500 ring-offset-1 focus:outline-none focus-visible:ring-1"
                  onClick={() => {
                    trackCustomEvent(TrackingEventsV2.KimEnableNewExperience, {
                      matterId,
                      subsetId,
                      from: 'Old Kim input',
                    });
                    localStorage.setItem('isUsingNewKim', 'true');
                  }}
                >
                  <Icon className="lucide--sparkle text-teal-400!" />
                  <span className="[animation:shimmer_3.5s_linear_infinite] bg-[linear-gradient(90deg,#4b5563_0%,#838994_40%,#4b5563_60%,#4b5563_100%)] [background-size:200%_100%] bg-clip-text text-transparent">
                    Go to the improved Kim experience
                  </span>

                  <Icon className="lucide--chevron-right -ml-0.75 duration-150 group-hover:ml-0.25" />
                </Link>
              </Button>

              <span className="mt-2.5 flex items-center gap-1.25 rounded-lg border border-amber-200 bg-amber-50 p-1.5 px-2 text-[13px] text-amber-600">
                <Icon className="lucide--info mb-px text-amber-500" />
                <p>
                  This version of Kim is being sunset on the 15th of June.{' '}
                  <button
                    onClick={() => setIsDeprecationDialogOpen(true)}
                    className="font-medium duration-150 hover:text-amber-700"
                  >
                    Read more.
                  </button>
                </p>
              </span>
            </div>

            <h2 className="mb-6 flex flex-col items-center gap-2.5 text-2xl font-semibold text-gray-800">
              <SubsetChip subsetId={subsetId} />
              <span className="pl-2">{getTitleFromMode(mode)}</span>
            </h2>

            <QuestionInput
              executeSearch={executeSearch}
              onChange={(v: string) => setQuestionValue(v)}
              value={questionValue}
              placeholder={placeholder}
              taggedDocuments={taggedDocuments}
              setTaggedDocuments={setTaggedDocuments}
              draftType={draftType}
              setDraftType={setDraftType}
              mode={mode}
              jurisdiction={subset?.jurisdiction ?? []}
              isCreatingThread={isCreatingThread || isCreatingMessage}
            />
            <ModeSelector
              mode={mode}
              setMode={setMode}
              setPlaceholder={setPlaceholder}
              documentCount={responseDocIds?.totalCount}
            />
          </div>
        )}
      </div>

      <NewKimDialog open={isNewKimDialogOpen} onOpenChange={setIsNewKimDialogOpen} subsetId={subsetId} />

      <KimDeprecationDialog
        open={isDeprecationDialogOpen}
        onOpenChange={setIsDeprecationDialogOpen}
        subsetId={subsetId}
      />

      {/* Manual Answer Modal */}
      <ManualAnswerModal
        isOpen={isManualAnswerModalOpen}
        onClose={() => setIsManualAnswerModalOpen(false)}
        onComplete={(threadId) => {
          setIsManualAnswerModalOpen(false);
          navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${threadId}`);
        }}
      />
    </div>
  );
}
